import type { IPublicClientApplication } from "@azure/msal-browser";
import { entradaEquipe } from "./config";

/**
 * Login Microsoft da equipe T4i. O MSAL só é baixado quando alguém usa a
 * entrada da equipe: o cliente, que entra com usuário e senha, nunca carrega
 * esse código.
 *
 * O portal não usa token da Microsoft para nada além da troca: o id_token vai
 * uma vez para POST /api/auth/microsoft, que devolve a sessão do portal, e o
 * cache do MSAL é limpo logo em seguida.
 */
let instancia: Promise<IPublicClientApplication> | null = null;

const msal = () => {
  instancia ??= (async () => {
    const { PublicClientApplication } = await import("@azure/msal-browser");
    const app = new PublicClientApplication({
      auth: {
        clientId: entradaEquipe.clientId,
        // Fixa no locatário da T4i: conta de fora a Microsoft já recusa.
        authority: `https://login.microsoftonline.com/${entradaEquipe.tenantId}`,
        // A raiz é o endereço registrado no Entra. A volta é levada para
        // /equipe em levarVoltaParaEquipe, antes do roteador.
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: "sessionStorage" },
    });
    await app.initialize();
    return app;
  })();
  return instancia;
};

const ESCOPOS = ["openid", "profile", "email"];

/** Sai para a Microsoft; a volta cai em /equipe. */
export const entrarComMicrosoft = async () => {
  const app = await msal();
  // Quem tem mais de uma conta Microsoft no navegador escolhe a da T4i.
  await app.loginRedirect({ scopes: ESCOPOS, prompt: "select_account" });
};

/** O id_token da volta do login, ou null quando esta carga não é uma volta. */
export const concluirVoltaDaMicrosoft = async (): Promise<string | null> => {
  const app = await msal();
  try {
    const resultado = await app.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    if (!resultado) return null;

    await app.clearCache();
    return resultado.idToken || null;
  } finally {
    // O MSAL limpa o fragmento da volta que reconhece; o que ele recusa
    // (estado velho, erro) ficaria na barra de endereço.
    if (window.location.hash)
      window.history.replaceState(null, "", window.location.pathname);
  }
};

/** Se a URL atual traz a resposta do Entra no fragmento. */
export const ehVoltaDaMicrosoft = (hash = window.location.hash) =>
  /[#&]state=/.test(hash) && /[#&](code|error)=/.test(hash);

/**
 * Chamada em main.tsx, antes do primeiro render. O Entra devolve na raiz, com
 * o resultado no fragmento; deixada ali, a raiz sem sessão iria para /login e o
 * fragmento se perderia. Nenhuma URL do cliente tem esse fragmento.
 */
export const levarVoltaParaEquipe = () => {
  if (window.location.pathname === "/" && ehVoltaDaMicrosoft())
    window.history.replaceState(null, "", `/equipe${window.location.hash}`);
};
